import { Injectable, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from './user.service';
import { User } from '../interfaces/user';
import { HotelService } from './hotel.service';
import { EventService } from './event.service';
import { LoggerService } from './logger.service';
import {
  AccessArea,
  InternalRole,
  ROUTE_ACCESS_MAP,
  ALWAYS_VISIBLE_ROUTES,
  INTERNAL_ONLY_ROUTES,
} from '../config/access-area.config';

/**
 * Permission service for the access area system.
 *
 * Two models:
 * - Hotel users: checked against access_areas (8 areas, multi-select)
 * - Internal users: checked against internal_role (3 roles, single-select)
 *
 * Internal users always have page-level access; their role determines edit vs view-only.
 *
 * Usage:
 * ```typescript
 * if (permissionService.hasAccessArea('bookings')) { ... }
 * if (permissionService.canEdit()) { ... }
 * if (permissionService.isInternal()) { ... }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private lastHotelId: number | null = null;

  constructor(
    private userService: UserService,
    private hotelService: HotelService,
    private eventService: EventService,
    private logger: LoggerService
  ) {
    this.setupHotelChangeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupHotelChangeListener(): void {
    this.eventService.managedHotelChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hotel => {
        if (hotel && hotel.id !== this.lastHotelId) {
          this.lastHotelId = hotel.id;
          this.refreshPermissions();
        }
      });
  }

  refreshPermissions(): void {
    this.logger.debug('Refreshing permissions for current hotel context');
    this.userService.resolveUser().subscribe({
      next: () => {
        this.logger.debug('Permissions refreshed successfully');
        this.eventService.changeUser(this.userService.getUser());
      },
      error: (err) => {
        this.logger.error('Failed to refresh permissions:', err);
      }
    });
  }

  private getCurrentUser(): User | null {
    return this.userService.getUser();
  }

  private getCurrentHotelId(): number | null {
    const hotelId = this.hotelService.hotelId;
    return hotelId ? parseInt(hotelId, 10) : null;
  }

  /**
   * Check if user has a specific access area.
   * Internal users always return true (they see all pages).
   */
  hasAccessArea(area: string): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }

    if (this.isInternal()) {
      return true;
    }

    if (user.access_areas && Array.isArray(user.access_areas)) {
      return user.access_areas.includes(area);
    }

    return false;
  }

  hasAnyAccessArea(...areas: string[]): boolean {
    if (!areas || areas.length === 0) {
      return false;
    }

    return areas.some(area => this.hasAccessArea(area));
  }

  canAccessRoute(routePath: string): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }

    if (ALWAYS_VISIBLE_ROUTES.includes(routePath)) {
      return true;
    }

    if (routePath in INTERNAL_ONLY_ROUTES) {
      if (!this.isInternal()) {
        return false;
      }
      const restriction = INTERNAL_ONLY_ROUTES[routePath];
      if (typeof restriction === 'string') {
        return this.getInternalRole() === restriction;
      }
      return true;
    }

    if (this.isInternal()) {
      return true;
    }

    const requiredArea = ROUTE_ACCESS_MAP[routePath];
    if (requiredArea) {
      return this.hasAccessArea(requiredArea);
    }

    // Unmapped route — deny by default (fail-safe)
    this.logger.warn(`Route "${routePath}" is not mapped in ROUTE_ACCESS_MAP. Access denied.`);
    return false;
  }

  /**
   * Check if user is an internal HotelCard staff member.
   * Internal users have a system-wide role.
   */
  isInternal(): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }

    return user.is_internal === true;
  }

  getInternalRole(): InternalRole | null {
    const user = this.getCurrentUser();
    if (!user || !user.internal_role) {
      return null;
    }

    return user.internal_role as InternalRole;
  }

  isSuperAdmin(): boolean {
    return this.getInternalRole() === InternalRole.SuperAdmin;
  }

  isAdmin(): boolean {
    return this.isSuperAdmin();
  }

  /**
   * Check if user can edit content on the current page.
   * - Archived hotel (the ambient selected hotel): false for everyone, even Super Admin
   * - Super Admin: always true
   * - Customer Success / Viewer: false (view-only for most pages)
   * - Hotel users with relevant access area: true
   */
  canEdit(): boolean {
    if (this.hotelService.selectedHotelSnapshot?.is_archived) {
      return false;
    }

    return this.canEditForRole();
  }

  /**
   * Same role check as canEdit(), but without the ambient-hotel archived
   * check. Use this where the relevant hotel is NOT the ambient selected
   * one — e.g. a cross-hotel view like All Bookings, where canEdit()'s
   * archived check would reflect whichever hotel happens to be selected
   * elsewhere in the app rather than the record actually being edited.
   * Combine with an explicit check against the correct hotel's own
   * archived state instead.
   */
  canEditForRole(): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }

    if (this.isSuperAdmin()) {
      return true;
    }

    if (this.isInternal()) {
      return false;
    }

    return true;
  }

  canEditInternalNotes(): boolean {
    if (this.hotelService.selectedHotelSnapshot?.is_archived) {
      return false;
    }

    const role = this.getInternalRole();
    return role === InternalRole.SuperAdmin || role === InternalRole.CustomerSuccess;
  }

  canUploadDocuments(): boolean {
    const role = this.getInternalRole();
    return role === InternalRole.SuperAdmin || role === InternalRole.CustomerSuccess;
  }

  isSetupComplete(): boolean {
    return !this.getSetupIncompleteReason();
  }

  /**
   * Returns the specific reason why setup is incomplete, or null if setup is complete.
   *
   * Possible reasons:
   * - 'no_role': Internal user without a role assigned
   * - 'no_hotels': Hotel user without any hotel assignments
   * - 'no_access_areas': Hotel user without any access areas
   */
  getSetupIncompleteReason(): string | null {
    const user = this.getCurrentUser();
    if (!user) {
      return 'no_user';
    }

    if (user.is_internal) {
      return user.internal_role ? null : 'no_role';
    }

    const hasHotels = Array.isArray(user.hotels) && user.hotels.length > 0;
    if (!hasHotels) {
      return 'no_hotels';
    }

    const hasAccessAreas = Array.isArray(user.access_areas) && user.access_areas.length > 0;
    if (!hasAccessAreas) {
      return 'no_access_areas';
    }

    return null;
  }

  /**
   * Get access areas the current user can assign to other users.
   * Users can only assign areas they themselves have.
   * Internal users can assign all areas.
   */
  getAssignableAccessAreas(): string[] {
    const user = this.getCurrentUser();
    if (!user) {
      return [];
    }

    if (this.isInternal()) {
      return Object.values(AccessArea);
    }

    return user.access_areas || [];
  }

  canAccessHotel(hotelId: number): boolean {
    const user = this.getCurrentUser();
    if (!user) {
      return false;
    }

    if (this.isInternal()) {
      return true;
    }

    if (user.hotels && Array.isArray(user.hotels)) {
      return user.hotels.some(hotel =>
        typeof hotel === 'object' ? hotel.id === hotelId : hotel === hotelId
      );
    }

    // Fail closed, matching canAccessRoute()'s default — a non-internal
    // user with no resolvable hotels list has no confirmed access to
    // anything, not the other way round.
    return false;
  }

  canAccessCurrentHotel(): boolean {
    const hotelId = this.getCurrentHotelId();
    if (!hotelId) {
      return false;
    }

    return this.canAccessHotel(hotelId);
  }

}
